import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { apiHost } from '../../../utils/apiHost';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = (apiHost() || "").replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const PERIODE_OPTIONS = [
  "januari", "februari", "maret", "april", "mei", "juni",
  "juli", "agustus", "september", "oktober", "november", "desember",
];

const CreateGajiKaryawan = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mengisi, setMengisi] = useState(false);
  const [users, setUsers] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [form, setForm] = useState({
    userUuid: "",
    tanggal_periode: new Date().toISOString().slice(0, 10),
    periode: PERIODE_OPTIONS[new Date().getMonth()],
    nominal: "",
    tambahan: "0",
    potongan: "0",
    keterangan: "",
  });

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${getApiBaseUrl()}/getusercabang`, { withCredentials: true });
      const data = res.data?.data || [];
      setUsers(data.filter((u) => u.role === "karyawan" || u.role === "kasir" || u.role === "dapur"));
    } catch (e) {
      setSnackbar({ open: true, message: "Gagal memuat data karyawan", severity: "error" });
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Isi nominal, potongan, tambahan & keterangan dari absensi bulan periode (GET /absensi/gaji-dari-absensi).
  const isiDariAbsensi = async () => {
    const pilih = users.find((u) => u.uuid === form.userUuid);
    if (pilih && pilih.role !== "karyawan") {
      setSnackbar({ open: true, message: "Isi dari absensi hanya untuk akun berperan karyawan", severity: "warning" });
      return;
    }
    const tahun = Number(String(form.tanggal_periode || "").slice(0, 4)) || new Date().getFullYear();
    const bulan = PERIODE_OPTIONS.indexOf(form.periode) + 1;
    setMengisi(true);
    try {
      const { data } = await axios.get(`${getApiBaseUrl()}/absensi/gaji-dari-absensi`, {
        params: { useruuid: form.userUuid, tahun, bulan }, withCredentials: true,
      });
      if (!data.adaGajiPokok) {
        setSnackbar({ open: true, message: "Gaji pokok karyawan belum diisi (Pengaturan Absensi › Shift & Jadwal, klik nama karyawan)", severity: "warning" });
        return;
      }
      setForm((f) => ({
        ...f,
        nominal: String(data.data.nominal),
        potongan: String(data.data.potongan),
        tambahan: String(data.data.tambahan),
        keterangan: data.data.keterangan,
      }));
      setSnackbar({
        open: true,
        severity: data.perluDicek ? "warning" : "success",
        message: data.perluDicek
          ? `Terisi. ${data.perluDicek} hari lupa absen pulang belum dihitung, periksa di Presensi.`
          : `Terisi dari absensi: diterima Rp ${Number(data.data.jumlah).toLocaleString("id-ID")}`,
      });
    } catch (e) {
      setSnackbar({ open: true, message: e?.response?.data?.message || "Gagal menghitung dari absensi", severity: "error" });
    } finally {
      setMengisi(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.userUuid || !form.nominal) {
      setSnackbar({ open: true, message: "Pilih karyawan dan isi nominal gaji", severity: "warning" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${getApiBaseUrl()}/gaji`, {
        userUuid: form.userUuid,
        tanggal_periode: form.tanggal_periode || null,
        periode: form.periode,
        nominal: Number(form.nominal) || 0,
        tambahan: Number(form.tambahan) || 0,
        potongan: Number(form.potongan) || 0,
        keterangan: form.keterangan || null,
      }, { withCredentials: true });
      Swal.fire({ title: "Berhasil", text: "Gaji berhasil dibuat", icon: "success" });
      navigate("/gaji-karyawan");
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Gagal membuat gaji";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Buat Gaji Karyawan
      </Typography>
      <Card sx={{ p: 3, maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Karyawan</InputLabel>
                <Select
                  name="userUuid"
                  value={form.userUuid}
                  label="Karyawan"
                  onChange={handleChange}
                >
                  {users.map((u) => (
                    <MenuItem key={u.uuid} value={u.uuid}>
                      {u.username} {u.Cabang ? `(${u.Cabang.namacabang})` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                name="tanggal_periode"
                label="Tanggal Periode"
                value={form.tanggal_periode}
                onChange={handleChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Periode</InputLabel>
                <Select name="periode" value={form.periode} label="Periode" onChange={handleChange}>
                  {PERIODE_OPTIONS.map((p) => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" onClick={isiDariAbsensi} disabled={!form.userUuid || mengisi}>
                {mengisi ? "Menghitung…" : "Isi dari absensi"}
              </Button>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                Menghitung nominal, potongan & tambahan dari absensi bulan periode (tahun dari Tanggal Periode), sesuai Pengaturan Absensi. Angka tetap bisa diubah.
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                name="nominal"
                label="Nominal Gaji"
                value={form.nominal}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                name="tambahan"
                label="Tambahan"
                value={form.tambahan}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                name="potongan"
                label="Potongan"
                value={form.potongan}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="keterangan"
                label="Keterangan"
                value={form.keterangan}
                onChange={handleChange}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                startIcon={loading ? <CircularProgress size={20} /> : <AddIcon />}
                disabled={loading}
              >
                Simpan Gaji
              </Button>
              <Button sx={{ ml: 1 }} onClick={() => navigate("/gaji-karyawan")}>
                Batal
              </Button>
            </Grid>
          </Grid>
        </form>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateGajiKaryawan;
